import { CoreApi, ProductInfoComponent, RangeComponent } from '@inter-ikea-storageone/range';
import {
  CommunicationSizeComponent,
  ComponentIds,
  Entity,
  HierarchyUtils
} from '@inter-ikea-core/core';
import { FrameSelectedLegComponent } from '@inter-ikea-storageone/range-besta';
import { hideNoFramesInSceneErrorMessage } from 'rangeConfig/shared/rules/hideMenuRules';
import { getFrontsInScene } from 'rangeConfig/shared/utils/frontLayouts';

/**
 * Only show supportlegs menu for selected frame if:
 * 1. Frame width is 1200 or 1800 and frame is floorstanding
 * 2. Current leg can have support leg
 * @param coreApi
 * @param selectedEntityId
 * @returns
 */
export const hideSupportLegsMenu = (coreApi: CoreApi, selectedEntityId: number) => {
  const selectedEntity = coreApi.entities.find(selectedEntityId);
  if (!selectedEntity) return true;
  const currentLeg =
    coreApi.entities
      .getScene()
      ?.getComponent<FrameSelectedLegComponent>(FrameSelectedLegComponent.id)?.legProductRef || '';
  const legsCantSelectSupportLeg = ['50293595', '10293597', '20293592', 'pFramePad'];
  if (legsCantSelectSupportLeg.includes(currentLeg)) {
    return true;
  }

  let frame;
  if (selectedEntity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
    frame = selectedEntity;
  } else if (selectedEntity.hasComponent(ComponentIds.LEG_COMPONENT_ID)) {
    frame =
      selectedEntity &&
      HierarchyUtils.findInUpwardsHierarchy(selectedEntity, (entity: Entity) =>
        entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
      );
  }

  if (
    frame &&
    coreApi.entities.getEntityComSize(frame)?.width !== 600 &&
    coreApi.entities.isEntityPlacedOnFloor(frame)
  ) {
    return false;
  }
  return true;
};

// Error to be displayed when user tries to add anything from EKET menu when only having BESTÅ frames in the scene
export const hideNoBestaFramesInSceneErrorMessage = (coreApi: CoreApi) => {
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

  const bestaFramesInScene = framesInScene?.filter(
    frame => frame.getComponent<RangeComponent>(RangeComponent.id)?.range === 'besta'
  );
  return bestaFramesInScene.length > 0 || !hideNoFramesInSceneErrorMessage(coreApi);
};

// Error to be displayed when user tries to add anything from EKET menu when only having BESTÅ frames in the scene
export const hideNoEketFramesInSceneErrorMessage = (coreApi: CoreApi) => {
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

  const eketFramesInScene = framesInScene?.filter(
    frame => frame.getComponent<RangeComponent>(RangeComponent.id)?.range === 'eket'
  );
  return eketFramesInScene.length > 0 || !hideNoFramesInSceneErrorMessage(coreApi);
};

// Error to be displayed when user tries to add anything from any menu when having a mix of frames in the scene
export const hideMixedBestaFramesInSceneErrorMessage = (coreApi: CoreApi) => {
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

  const eketFramesInScene = framesInScene?.filter(
    frame => frame.getComponent<RangeComponent>(RangeComponent.id)?.range === 'eket'
  );
  const mixedFrames =
    framesInScene.length - eketFramesInScene.length > 0 && eketFramesInScene.length > 0;

  return !mixedFrames;
};

// Error to be displayed when user tries to add top panels when not having any frames in the scene that can have top panels
export const hideBestaNoTopPanelFramesInSceneErrorMessage = (coreApi: CoreApi) => {
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

  const bestaFramesInScene = framesInScene?.filter(
    frame => frame.getComponent<RangeComponent>(RangeComponent.id)?.range === 'besta'
  );

  const frameDepths = bestaFramesInScene.map(
    frame =>
      frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
        ?.size?.depth
  );

  const uniqueDepths = Array.from(new Set(frameDepths));
  const only20DeepFrames = uniqueDepths.length === 1 && uniqueDepths[0] === 200;
  return !only20DeepFrames;
};

// Error to be displayed when user tries to add lights when only having mix of solid and transparent doors in the scene
export const hideAddingLightsToBestaMixedDoorsErrorMessage = (coreApi: CoreApi) => {
  const doorsInScene = getFrontsInScene(coreApi);
  const transparentDoorsInScene = doorsInScene.filter(d =>
    d.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design?.includes('glass')
  );

  const mixSolidTransparentDoors =
    transparentDoorsInScene.length > 0 && doorsInScene.length - transparentDoorsInScene.length > 0;

  return !mixSolidTransparentDoors;
};

// Error to be displayed when user tries to add lights when only having solid doors in the scene
export const hideAddingLightsToBestaSolidDoorsErrorMessage = (coreApi: CoreApi) => {
  const doorsInScene = getFrontsInScene(coreApi);
  const transparentDoorsInScene = doorsInScene.filter(d =>
    d.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design?.includes('glass')
  );

  const onlySolidDoors = doorsInScene.length > 0 && transparentDoorsInScene.length === 0;

  return !onlySolidDoors;
};
